/** @jsxImportSource theme-ui @jsxRuntime classic */
import React, { useMemo } from 'react';
import {
  Container,
  Section,
  Box,
  Flex,
  Text,
  IntrinsicImage,
  CarouselV2ContextProvider,
  CarouselV2,
  CarouselNavigationV2,
  useThemeUIValue,
} from '@tishman/components';

import { formatStoryCategory } from '../utils';

import type { ComponentPropsWithoutRef } from 'react';

const StoryRelatedStoriesBlock = (
  props: { data: Queries.SanityStoryQuery } & ComponentPropsWithoutRef<
    typeof Section
  >,
): React.JSX.Element => {
  const { story, recentStories } = props.data;

  const relatedStoriesProps = useMemo(() => {
    if (!story?.featuredStories || story?.featuredStories.length === 0) {
      // If the story does not have  featured stories (related stories).
      // display 2 most recent stories in this block instead
      if (!recentStories?.nodes)
        throw new Error('Expected valid recent stories data');

      const stories = recentStories.nodes.map((node) => {
        return {
          title: node?.titleAndSlug?.title,
          excerpt: node?.excerpt,
          url: `/magazine/${formatStoryCategory(story?.category ?? '')}/${
            story?.titleAndSlug?.slug?.current
          }`,
          alt: node?.poster?.alt,
          image: node?.poster?.asset?.gatsbyImageData,
          category: node?.category,
          formattedPublishAt: node?.formattedPublishAt,
        };
      });
      return {
        title: 'Recent',
        stories,
      };
    }
    // else show the featured stories (related stories) in this block
    const stories = story.featuredStories.map((story) => {
      return {
        title: story?.titleAndSlug?.title,
        excerpt: story?.excerpt,
        url: `/magazine/${story?.category}/${story?.titleAndSlug?.slug?.current}`,
        alt: story?.poster?.alt,
        image: story?.poster?.asset?.gatsbyImageData,
        category: story?.category,
        formattedPublishAt: story?.formattedPublishAt,
      };
    });
    return {
      title: 'Related Articles',
      stories,
    };
  }, [story, recentStories]);

  const slideSpaceBetween = useThemeUIValue([3, 3, 4], 'space');
  const carouselHorizontalSpacing = useThemeUIValue([3, 3, 4], 'space');

  return (
    relatedStoriesProps && (
      <Section data-section="StoryRelatedStories" {...props}>
        <Container sx={{ maxWidth: 1400, margin: '0 auto', py: 5 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Text
              sx={{
                mt: 0,
                mb: 4,
                ml: [3, 4, 'initial'],
                fontFamily: 'headingSecondary',
                textAlign: ['initial', null, 'center'],
              }}
              variant="featuredStoryTitleSmall"
            >
              {relatedStoriesProps.title}
            </Text>
          </Box>
          <CarouselV2ContextProvider>
            <CarouselV2
              containerStyle={{ maxWidth: '1200px' }}
              slideStyle={{ maxWidth: '280px' }}
              swiperOptions={{
                spaceBetween: slideSpaceBetween,
                style: {
                  paddingLeft: carouselHorizontalSpacing,
                  paddingRight: carouselHorizontalSpacing,
                },
              }}
            >
              {relatedStoriesProps.stories.map((article) => (
                <Container
                  key={article.title}
                  sx={{
                    maxWidth: ['calc(220 / 322 * 100vw)', '280px'],
                    minHeight: [null, null, '500px'],
                    minWidth: '257px',
                    width: '100%',
                    px: [0, 0, null],
                    pt: 2,
                    mx: 0,
                  }}
                >
                  <Flex sx={{ flexDirection: 'column', width: '100%' }}>
                    <Flex
                      sx={{
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%',
                        display: ['flex', null, null, null, 'none'],
                      }}
                    >
                      <Text
                        as="span"
                        onClick={() =>
                          (location.href = `/magazine/#${article.category
                            ?.toLowerCase()
                            .replace(/\s/g, '')
                            .replace('&', '-')}`)
                        }
                        sx={{ mb: 3, cursor: 'pointer' }}
                        variant="featuredStoryEyebrow"
                      >
                        {article.category}
                      </Text>
                      <Text
                        as="span"
                        sx={{
                          mt: 0,
                          mb: 3,
                        }}
                        variant="storyDate"
                      >
                        {article.formattedPublishAt}
                      </Text>
                    </Flex>
                    <Box
                      className="image-box"
                      onClick={() => (location.href = `${article.url}`)}
                      sx={{
                        'width': '100%',
                        'ml': '-10px',
                        'mb': 3,
                        'transition': '.5s ease all',
                        'p': '10px',
                        'border': '1px solid transparent',
                        'overflow': 'hidden',
                        'boxSizing': 'content-box',
                        'cursor': 'pointer',
                        '&:hover .feature-image': {
                          transform: 'scale(1.05)',
                          opacity: 0.8,
                        },
                        '&:hover': {
                          borderColor: 'CREAM',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          height: '100%',
                          width: '100%',
                          overflow: 'hidden',
                        }}
                      >
                        {article.image && (
                          <IntrinsicImage
                            alt={article.alt ?? ''}
                            className="feature-image"
                            image={article.image}
                            sx={{
                              transition: '.5s ease all',
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                    <Flex
                      sx={{
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%',
                        display: ['none', null, null, null, 'flex'],
                      }}
                    >
                      <Text
                        as="span"
                        onClick={() =>
                          (location.href = `/magazine/#${article.category
                            ?.toLowerCase()
                            .replace(/\s/g, '')
                            .replace('&', '-')}`)
                        }
                        sx={{ mb: 0, cursor: 'pointer' }}
                        variant="featuredStoryEyebrow"
                      >
                        {article.category}
                      </Text>
                      <Text
                        as="span"
                        sx={{
                          mt: 0,
                          mb: 3,
                        }}
                        variant="storyDate"
                      >
                        {article.formattedPublishAt}
                      </Text>
                    </Flex>
                    <Text
                      as="h3"
                      onClick={() => (location.href = `${article.url}`)}
                      sx={{
                        'variant': 'text.featuredStoryTitleSmall',
                        'fontFamily': 'headingSecondary',
                        'textAlign': 'left',
                        'display': 'block',
                        'cursor': 'pointer',
                        '&:hover': {
                          color: 'CREAM',
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      {article.title}
                    </Text>
                  </Flex>
                </Container>
              ))}
            </CarouselV2>
            <Container
              sx={{
                alignSelf: 'center',
                display: ['flex', null, null, null, null, 'none'],
                justifyContent: 'flex-end',
                mt: 4,
                mb: 3,
                position: 'static',
              }}
            >
              <CarouselNavigationV2 />
            </Container>
          </CarouselV2ContextProvider>
        </Container>
      </Section>
    )
  );
};

export default StoryRelatedStoriesBlock;
